// 🧰 **모션 부품 6개** — 전부 `(ctx, box, t, input) => void` 순수 그리기(2026-09-28 · 1단계).
//
// ★부품은 «자리»를 모른다★ — 어디에 그릴지는 호출자가 `box`(px 사각형)와 `input` 의 좌표로 준다.
//   부품이 스스로 캔버스 크기를 읽거나 세이프존을 계산하면 조립(`card.ts`)과 검사(`measure.ts`)가
//   같은 자리를 두 번 계산하게 된다. 시간 `t` 는 호출자의 단위(M 컷은 `t01` 0~1)를 그대로 받는다.
// ★t 가 1 을 지나면 모든 부품이 «완성 상태»★ — 카드 사진 굽기(t01=1)와 마지막 프레임이 같은 그림이다.
// ★캔버스 상태는 `save/restore` 로 되돌린다★ — 부품이 남긴 alpha·font·transform 이 다음 부품에 새면
//   그리는 순서가 곧 상태가 되어 결정성이 깨진다.
// 참조 구현: `~/맥공유/모션스타일-목업-20260927/mock.html`(캔버스 2D · 720×1280).
import { EASE_ENTER, EASE_MOVE, UI_SPRING, clamp01, lin, springStep } from "./spring";

export interface KitBox { x: number; y: number; w: number; h: number }

/** 모서리 둥근 사각형 경로(채우기·선은 호출자가). `cinematic-motion-draw.roundRect` 와 같은 산식이다. */
export function kitRoundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

/** 두 겹 그림자 글자(설계서 4절 가독성 규칙 — 가는 테두리 대신 그림자). */
export function shadowText(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, px: number): void {
  ctx.save();
  ctx.shadowColor = "rgba(0,0,0,.35)";
  ctx.shadowOffsetY = Math.max(1, px * 0.05);
  ctx.fillText(text, x, y);
  ctx.restore();
  ctx.save();
  ctx.shadowColor = "rgba(0,0,0,.45)";
  ctx.shadowBlur = px * 0.4;
  ctx.shadowOffsetY = px * 0.12;
  ctx.fillText(text, x, y);
  ctx.restore();
}

/* ── ① ColorCut — 단색 배경이 아래→위로 쓸린다 ───────────────────────────── */
export interface ColorCutInput {
  color: string;
  /** 쓸리기 전 바탕. 없으면 목표색의 45%(검정 금지 — 검은 프레임 0). */
  base?: string;
  start: number;
  /** 쓸림에 걸리는 시간(호출자 단위). 목업은 0.18s. */
  dur: number;
  /** 쓸림 뒤 배경 숨쉬기 주기(호출자 단위). 없으면 정지. */
  pulsePeriod?: number;
}
/** `#rrggbb` 를 `f`(0~1)배로 어둡게. 못 읽으면 그대로. */
export function darkenHex(hex: string, f: number): string {
  const m = /^#([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return hex;
  const n = parseInt(m[1], 16);
  const c = (v: number) => Math.max(0, Math.min(255, Math.round(v * f))).toString(16).padStart(2, "0");
  return `#${c(n >> 16)}${c((n >> 8) & 255)}${c(n & 255)}`;
}

/** ★검은 프레임 0★(설계서 5절 5 · 부모 세션 육안 09-28) — 쓸리기 전 바탕은 검정이 아니라 **목표색의 45%** 다. 0초 프레임도 그 색이다. */
export function colorCut(ctx: CanvasRenderingContext2D, box: KitBox, t: number, input: ColorCutInput): void {
  ctx.save();
  ctx.globalAlpha = 1;
  ctx.fillStyle = input.base ?? darkenHex(input.color, 0.45);
  ctx.fillRect(box.x, box.y, box.w, box.h);
  const p = EASE_ENTER(lin(t, input.start, input.start + input.dur));
  if (p > 0) {
    ctx.fillStyle = input.color;
    ctx.fillRect(box.x, box.y + box.h * (1 - p), box.w, box.h * p);
  }
  // 🫧 «완전 정지 ≤ 2.5초»(GNWORKS 3-1) — 쓸림이 끝난 뒤 배경이 아주 천천히 숨 쉰다(±2% 밝기 · 주기 `pulsePeriod`).
  //   휘도 급변(10% 초과) 검사엔 안 걸리는 크기다. `pulsePeriod` 가 없으면 숨 쉬지 않는다.
  if (p >= 1 && input.pulsePeriod && input.pulsePeriod > 0) {
    const phase = ((t - input.start - input.dur) / input.pulsePeriod) * Math.PI * 2;
    const a = 0.02 * (1 + Math.sin(phase)) / 2;
    if (a > 0) {
      ctx.fillStyle = `rgba(255,255,255,${a.toFixed(4)})`;
      ctx.fillRect(box.x, box.y, box.w, box.h);
    }
  }
  ctx.restore();
}

/* ── ② Kinetic — 한 글자씩 스프링 등장 ────────────────────────────────────── */
export interface KineticLine { text: string; x: number; y: number }
export interface KineticInput {
  /** 이미 줄바꿈된 줄들(조립기가 `motion-card-layout` 산식으로 접는다). */
  lines: readonly KineticLine[];
  font: string;
  px: number;
  color: string;
  start: number;
  /** 글자 사이 지연. 목업 0.035s. */
  perChar: number;
  /** 한 글자가 다 나타나는 데 걸리는 시간. 목업 0.32s. */
  dur: number;
  /** 줄 사이 추가 지연(다음 줄 첫 글자 = 앞 줄 마지막 글자 + perChar). 기본 0. */
  lineGap?: number;
}
/** 마지막 글자가 시작되는 시각을 돌려준다(다음 부품이 이어 붙일 때 쓴다). */
export function kinetic(ctx: CanvasRenderingContext2D, _box: KitBox, t: number, input: KineticInput): number {
  ctx.save();
  ctx.font = input.font;
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  ctx.fillStyle = input.color;
  let start = input.start;
  for (const line of input.lines) {
    let cx = line.x;
    for (const ch of Array.from(line.text)) {
      const w = ctx.measureText(ch).width;
      const p = lin(t, start, start + input.dur);
      if (p > 0) {
        // ★큰 글자는 튀어나감 없음★ — 진입 이징으로 0.92→1 배율 + 살짝 올라오기(GNWORKS 3-1 · 연구일지 4절).
        const e = EASE_ENTER(p);
        const s = 0.92 + 0.08 * e;
        const cxm = cx + w / 2;
        const cym = line.y + input.px;
        ctx.save();
        ctx.globalAlpha = clamp01(e * 1.6);
        ctx.translate(cxm, cym);
        ctx.scale(s, s);
        ctx.translate(-cxm, -cym);
        ctx.translate(0, (1 - e) * input.px * 0.45);
        shadowText(ctx, ch, cx, line.y, input.px);
        ctx.restore();
      }
      cx += w;
      start += input.perChar;
    }
    start += input.lineGap ?? 0;
  }
  ctx.restore();
  return start;
}

/* ── ③ PairCopy — 둘째 줄이 «반박»처럼 올라오고 흰 띠가 깔린다 ─────────────── */
export interface PairCopyInput {
  lines: readonly KineticLine[];
  font: string;
  px: number;
  /** 띠 위 글자색(브랜드색). 띠가 반쯤 깔리기 전엔 흰 글자. */
  accent: string;
  start: number;
  /** 올라오는 데 걸리는 시간. 목업 0.3s. 띠는 그 뒤 0.25s 부터 0.3s 동안. */
  dur: number;
  /** 잘라 보이는 창(올라오는 동안 위쪽이 새지 않게). */
  clip: KitBox;
}
export function pairCopy(ctx: CanvasRenderingContext2D, _box: KitBox, t: number, input: PairCopyInput): void {
  const p = lin(t, input.start, input.start + input.dur);
  if (p <= 0) return;
  ctx.save();
  ctx.beginPath();
  ctx.rect(input.clip.x, input.clip.y, input.clip.w, input.clip.h);
  ctx.clip();
  ctx.translate(0, (1 - EASE_ENTER(p)) * input.px * 2.7);
  ctx.font = input.font;
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  const hp = EASE_MOVE(lin(t, input.start + input.dur * 0.83, input.start + input.dur * 1.83));
  for (const line of input.lines) {
    const w = ctx.measureText(line.text).width;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(line.x - input.px * 0.19, line.y - input.px * 0.11, (w + input.px * 0.38) * hp, input.px * 1.32);
    ctx.fillStyle = hp > 0.5 ? input.accent : "#ffffff";
    ctx.fillText(line.text, line.x, line.y + input.px * 0.1);
  }
  ctx.restore();
}

/* ── ④ StepReveal — 단계가 **누적**되며 쌓인다 + 진행 막대 + 가운데 도형(번호 라벨 없음) ── */
export interface StepRevealInput {
  /** 단계 캡션(원문 줄 · 접힘은 조립기가). 자리는 조립기가 준 그대로 — 지난 단계도 그 자리에 남는다. */
  steps: ReadonlyArray<readonly KineticLine[]>;
  /** 각 단계가 시작되는 시각(오름차순). */
  beats: readonly number[];
  /** 한 단계가 자리 잡는 시간. */
  dur: number;
  font: string;
  px: number;
  color: string;
  /** 진행 막대 자리(가로 전체). */
  bar: KitBox;
  /** 가운데 그림 중심과 크기. */
  figure: { cx: number; cy: number; size: number };
  /** 캡션이 올라오는 창(전 단계 묶음). */
  captionClip: KitBox;
}
/**
 * ★교체가 아니라 누적★(GNWORKS 3-1 «3개 이상 나열은 구간 끝까지 화면 유지») — 지난 단계 캡션은 그대로(alpha 1) 남고,
 * 현재 단계만 진입 이징으로 올라온다. 그래서 완성 프레임(사진 굽기)에 모든 단계가 **온전히**(완료 조건 ③) 보인다.
 */
export function stepReveal(ctx: CanvasRenderingContext2D, _box: KitBox, t: number, input: StepRevealInput): void {
  const n = Math.min(input.steps.length, input.beats.length);
  if (n === 0) return;
  let step = -1;
  for (let i = 0; i < n; i++) if (t >= input.beats[i]) step = i;
  ctx.save();
  const gap = input.bar.h * 1.2;
  const cellW = (input.bar.w - gap * (n - 1)) / n;
  for (let i = 0; i < n; i++) {
    const x = input.bar.x + i * (cellW + gap);
    ctx.fillStyle = "rgba(255,255,255,.28)";
    kitRoundRect(ctx, x, input.bar.y, cellW, input.bar.h, input.bar.h / 2);
    ctx.fill();
    const f = i < step ? 1 : i === step ? EASE_MOVE(lin(t, input.beats[i], input.beats[i] + input.dur)) : 0;
    if (f > 0) {
      ctx.fillStyle = "#ffffff";
      kitRoundRect(ctx, x, input.bar.y, cellW * f, input.bar.h, input.bar.h / 2);
      ctx.fill();
    }
  }
  if (step >= 0) {
    const p = EASE_ENTER(lin(t, input.beats[step], input.beats[step] + input.dur));
    const s = input.figure.size;
    if (s > 0) {
      ctx.save();
      ctx.translate(input.figure.cx, input.figure.cy);
      ctx.scale(0.9 + 0.1 * p, 0.9 + 0.1 * p);
      ctx.globalAlpha = p;
      ctx.fillStyle = "#ffffff";
      const shape = step % 3;
      if (shape === 0) {
        kitRoundRect(ctx, -s * 0.39, -s * 0.5, s * 0.78, s, s * 0.065);
        ctx.fill();
        ctx.fillStyle = input.color;
        for (let k = 0; k < 5; k++) {
          kitRoundRect(ctx, -s * 0.285, -s * 0.357 + k * s * 0.157, s * 0.57 - k * s * 0.071, s * 0.05, s * 0.025);
          ctx.fill();
        }
      } else if (shape === 1) {
        for (let k = 0; k < 3; k++) {
          ctx.save();
          ctx.rotate((k - 1) * 0.12);
          ctx.fillStyle = k === 1 ? "#ffffff" : "rgba(255,255,255,.7)";
          kitRoundRect(ctx, -s * 0.6 + k * s * 0.071, -s * 0.46 + k * s * 0.285, s * 1.07, s * 0.228, s * 0.05);
          ctx.fill();
          ctx.restore();
        }
      } else {
        kitRoundRect(ctx, -s * 0.357, -s * 0.607, s * 0.714, s * 1.214, s * 0.085);
        ctx.fill();
        ctx.fillStyle = input.color;
        ctx.beginPath();
        ctx.moveTo(-s * 0.093, -s * 0.143);
        ctx.lineTo(s * 0.143, 0);
        ctx.lineTo(-s * 0.093, s * 0.143);
        ctx.closePath();
        ctx.fill();
      }
      ctx.restore();
    }
    ctx.save();
    ctx.beginPath();
    ctx.rect(input.captionClip.x, input.captionClip.y, input.captionClip.w, input.captionClip.h);
    ctx.clip();
    ctx.font = input.font;
    ctx.textAlign = "left";
    ctx.textBaseline = "top";
    for (let i = 0; i <= step; i++) {
      const q = i < step ? 1 : p;
      ctx.globalAlpha = q;
      ctx.fillStyle = "#ffffff";
      const lift = (1 - q) * input.px * 1.2;
      for (const line of input.steps[i]) shadowText(ctx, line.text, line.x, line.y + lift, input.px);
    }
    ctx.restore();
  }
  ctx.restore();
}

/* ── ⑤ CheckList — 체크박스가 순서대로 켜진다 ─────────────────────────────── */
export interface CheckRow {
  /** 행 사각형(반투명 판). */
  box: KitBox;
  /** 이 행의 글자 줄(접힌 뒤). */
  lines: readonly KineticLine[];
  start: number;
}
export interface CheckListInput {
  rows: readonly CheckRow[];
  /** 행이 자리 잡는 시간. 목업 0.25s(체크는 그 뒤 반 박자). */
  dur: number;
  font: string;
  px: number;
  /** 체크 표시 색(브랜드색). */
  accent: string;
  /** 호출자 시간 단위 1 이 몇 초인가(`t01` 이면 컷 길이). 있으면 체크 표시가 실제 초 기준 UI 스프링으로 튄다. */
  tickSec?: number;
}
export function checkList(ctx: CanvasRenderingContext2D, _box: KitBox, t: number, input: CheckListInput): void {
  ctx.save();
  ctx.font = input.font;
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  for (const row of input.rows) {
    const p = lin(t, row.start, row.start + input.dur);
    if (p <= 0) continue;
    // ✔ 체크 표시는 UI 소품 — 스프링(살짝 튀어나감) 허용. `tickSec` 가 실제 초 단위 경과다(조립기가 준다).
    const tickTau = input.tickSec ? Math.max(0, (t - (row.start + input.dur * 0.48)) * input.tickSec) : lin(t, row.start + input.dur * 0.48, row.start + input.dur * 1.4);
    const q = input.tickSec ? clamp01(springStep(tickTau, UI_SPRING.stiffness, UI_SPRING.damping, UI_SPRING.mass)) : tickTau;
    const b = row.box;
    const cb = input.px * 1.35; // 체크박스 한 변
    const cbx = b.x + input.px * 0.7;
    const cby = b.y + (b.h - cb) / 2;
    ctx.save();
    ctx.globalAlpha = clamp01(p * 2);
    ctx.translate((1 - EASE_ENTER(p)) * -input.px * 1.8, 0);
    ctx.fillStyle = "rgba(255,255,255,.16)";
    kitRoundRect(ctx, b.x, b.y, b.w, b.h, input.px * 0.5);
    ctx.fill();
    ctx.strokeStyle = "#ffffff";
    ctx.lineWidth = Math.max(2, input.px * 0.15);
    kitRoundRect(ctx, cbx, cby, cb, cb, cb * 0.22);
    ctx.stroke();
    if (q > 0) {
      ctx.fillStyle = "#ffffff";
      kitRoundRect(ctx, cbx, cby, cb, cb, cb * 0.22);
      ctx.save();
      ctx.globalAlpha = clamp01(q * 3) * clamp01(p * 2);
      ctx.fill();
      ctx.restore();
      ctx.strokeStyle = input.accent;
      ctx.lineWidth = Math.max(3, input.px * 0.21);
      ctx.lineCap = "round";
      ctx.beginPath();
      const pts: Array<[number, number]> = [
        [cbx + cb * 0.25, cby + cb * 0.52],
        [cbx + cb * 0.45, cby + cb * 0.75],
        [cbx + cb * 0.8, cby + cb * 0.27],
      ];
      const n = q * 2;
      ctx.moveTo(pts[0][0], pts[0][1]);
      ctx.lineTo(pts[0][0] + (pts[1][0] - pts[0][0]) * clamp01(n), pts[0][1] + (pts[1][1] - pts[0][1]) * clamp01(n));
      if (n > 1) ctx.lineTo(pts[1][0] + (pts[2][0] - pts[1][0]) * clamp01(n - 1), pts[1][1] + (pts[2][1] - pts[1][1]) * clamp01(n - 1));
      ctx.stroke();
    }
    ctx.fillStyle = "#ffffff";
    for (const line of row.lines) ctx.fillText(line.text, line.x, line.y);
    ctx.restore();
  }
  ctx.restore();
}

/* ── ⑥ Stamp — 2.4배에서 1배로 «쾅»(UI 스프링 착지 · 흰 번쩍 없음) ───────────── */
export interface StampInput {
  text: string;
  font: string;
  px: number;
  cx: number;
  cy: number;
  start: number;
  /** 내려앉는 시간(호출자 단위). */
  dur: number;
  /** 도장 테두리 폭(가로 반쪽). 세로 반쪽은 px 로 정한다. */
  halfW: number;
  /** 호출자 시간 단위 1 이 몇 초인가. 있으면 착지가 실제 초 기준 UI 스프링. */
  tickSec?: number;
}
/** ★흰 플래시는 두지 않는다★(GNWORKS 3-1 «흰 플래시 0» · 종전 목업의 번쩍은 뺐다). */
export function stamp(ctx: CanvasRenderingContext2D, _box: KitBox, t: number, input: StampInput): void {
  const sp = lin(t, input.start, input.start + input.dur);
  if (sp <= 0) return;
  const land = input.tickSec
    ? clamp01(springStep(Math.max(0, (t - input.start) * input.tickSec), UI_SPRING.stiffness, UI_SPRING.damping, UI_SPRING.mass))
    : EASE_ENTER(sp);
  const s = 2.4 - 1.4 * land;
  const halfH = input.px * 0.9;
  ctx.save();
  ctx.translate(input.cx, input.cy);
  ctx.rotate(-0.14);
  ctx.scale(s, s);
  ctx.globalAlpha = clamp01(sp * 3);
  ctx.strokeStyle = "#ffffff";
  ctx.lineWidth = Math.max(3, input.px * 0.11);
  kitRoundRect(ctx, -input.halfW, -halfH, input.halfW * 2, halfH * 2, input.px * 0.25);
  ctx.stroke();
  ctx.font = input.font;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillStyle = "#ffffff";
  ctx.fillText(input.text, 0, input.px * 0.06);
  ctx.restore();
}
